<template>
  <div class="content-page">
    <page-skeleton v-if="loading" variant="detail" class="mt-4" />

    <LessonContent
      v-else
      :lesson="module"
      :initial-material-id="requestedMaterialId"
      :notes-hint="
        isTraining
          ? $t('teacherNotes.hintTraining')
          : $t('teacherNotes.hintShare')
      "
      :completed="completed"
      :can-complete="isTraining"
      :completing="completing"
      @back="goBack"
      @toggle-complete="setCompletion"
    >
      <template #notes>
        <div class="notes">
          <div class="composer">
            <h4>{{ $t("teacherNotes.newNote") }}</h4>
            <!-- Card 86akptec0: treinamento é exclusivo dos professores, então
                 a anotação fica só para ele (a API recusa compartilhar). -->
            <p v-if="isTraining">
              {{ $t("teacherNotes.trainingText") }}
            </p>
            <p v-else>
              {{ $t("teacherNotes.shareText") }}
            </p>
            <textarea
              v-model="draft"
              :placeholder="
                isTraining
                  ? $t('teacherNotes.placeholderTraining')
                  : $t('teacherNotes.placeholderShare')
              "
              rows="4"
              :maxlength="maxLength"
            ></textarea>
            <!-- Anexos da anotação nova. Sobem no ato da seleção para que o
                 professor veja o erro de tipo/tamanho antes de escrever tudo. -->
            <div class="attachments">
              <button
                type="button"
                class="attachments__add"
                :disabled="uploading || draftFiles.length >= maxFiles"
                @click="pickFiles"
              >
                <i class="mdi mdi-paperclip"></i>
                {{
                  uploading
                    ? $t("teacherNotes.sending")
                    : $t("teacherNotes.attach")
                }}
              </button>
              <span class="attachments__hint">
                {{
                  $t("teacherNotes.attachHint", {
                    count: maxFiles,
                    size: maxFileSizeMb,
                  })
                }}
              </span>

              <input
                ref="fileInput"
                type="file"
                class="attachments__input"
                :accept="acceptedTypes"
                @change="onFilesPicked"
              />

              <ul v-if="draftFiles.length" class="attachments__list">
                <li v-for="file in draftFiles" :key="file.id">
                  <i class="mdi mdi-file-outline"></i>
                  <span class="attachments__name">{{ file.name }}</span>
                  <button
                    type="button"
                    :title="$t('teacherNotes.removeAttachment')"
                    @click="removeDraftFile(file)"
                  >
                    <i class="mdi mdi-close"></i>
                  </button>
                </li>
              </ul>
            </div>

            <!-- Card 17trt6jfxtn: aulas da biblioteca, de qualquer nível, que
                 chegam ao aluno junto da anotação. -->
            <div v-if="!isTraining" class="extras">
              <button
                type="button"
                class="extras__add"
                :disabled="saving"
                @click="openExtraPickerForNew"
              >
                <i class="mdi mdi-book-plus-outline"></i>
                {{ $t("extraContent.add") }}
              </button>
              <span v-for="extra in draftExtras" :key="extra.id" class="chip">
                {{ extra.title }}
                <button
                  type="button"
                  :title="$t('extraContent.remove')"
                  @click="removeDraftExtra(extra)"
                >
                  <i class="mdi mdi-close"></i>
                </button>
              </span>
            </div>

            <div class="composer__foot">
              <span class="composer__count">
                {{ draft.length }}/{{ maxLength }}
              </span>
              <div class="composer__actions">
                <button
                  class="composer__btn composer__btn--ghost"
                  type="button"
                  :disabled="!draft.trim() || saving"
                  @click="createPrivate"
                >
                  {{
                    isTraining
                      ? $t("teacherNotes.saveNote")
                      : $t("teacherNotes.saveForMe")
                  }}
                </button>
                <button
                  v-if="!isTraining"
                  class="composer__btn"
                  type="button"
                  :disabled="!draft.trim() || saving"
                  @click="openShareForNew"
                >
                  <i class="mdi mdi-account-multiple-plus-outline"></i>
                  {{ $t("teacherNotes.share") }}
                </button>
                <!-- Card 86akj6ghg: mesmo texto, mesmos anexos, mesma escolha
                     de turma/alunos. O que muda é só onde aparece para o
                     aluno — e que ele pode marcar como concluído. -->
                <button
                  v-if="!isTraining"
                  class="composer__btn composer__btn--homework"
                  type="button"
                  :disabled="!draft.trim() || saving"
                  @click="openHomeworkForNew"
                >
                  <i class="mdi mdi-notebook-edit-outline"></i>
                  {{ $t("teacherNotes.sendHomework") }}
                </button>
              </div>
            </div>
          </div>

          <div v-if="notes.length" class="board">
            <article v-for="note in notes" :key="note.id" class="tnote">
              <div v-if="editingId === note.id" class="tnote__edit">
                <textarea
                  v-model="editingText"
                  rows="4"
                  :maxlength="maxLength"
                ></textarea>
                <div class="tnote__edit-actions">
                  <button type="button" @click="cancelEdit">
                    {{ $t("common.cancel") }}
                  </button>
                  <button
                    type="button"
                    class="is-primary"
                    :disabled="!editingText.trim() || saving"
                    @click="saveEdit(note)"
                  >
                    {{ $t("common.save") }}
                  </button>
                </div>
              </div>

              <template v-else>
                <span v-if="note.kind === 'homework'" class="tnote__kind">
                  <i class="mdi mdi-notebook-edit-outline"></i>
                  {{ $t("teacherNotes.homework") }}
                </span>
                <linkified-text tag="p" class="tnote__body" :text="note.note" />

                <!-- Os anexos seguem a permissão da anotação: quem recebe a
                     anotação recebe os arquivos. -->
                <ul v-if="note.files && note.files.length" class="tnote__files">
                  <li v-for="file in note.files" :key="file.id">
                    <a :href="file.url" target="_blank" rel="noopener">
                      <i class="mdi mdi-file-outline"></i>
                      {{ file.name }}
                    </a>
                    <button
                      type="button"
                      :title="$t('teacherNotes.detach')"
                      :disabled="saving"
                      @click="detachFile(note, file)"
                    >
                      <i class="mdi mdi-close"></i>
                    </button>
                  </li>
                </ul>

                <div
                  v-if="!isTraining && note.extras && note.extras.length"
                  class="tnote__extras"
                >
                  <span class="tnote__shares-label">
                    {{ $t("extraContent.noteLabel") }}
                  </span>
                  <span
                    v-for="extra in note.extras"
                    :key="extra.id"
                    class="chip"
                  >
                    {{ extra.title }}
                    <button
                      type="button"
                      :title="$t('extraContent.remove')"
                      :disabled="saving"
                      @click="detachExtra(note, extra)"
                    >
                      <i class="mdi mdi-close"></i>
                    </button>
                  </span>
                </div>

                <div v-if="!isTraining" class="tnote__shares">
                  <span v-if="!note.shares.length" class="tnote__private">
                    <i class="mdi mdi-lock-outline"></i>
                    {{ $t("teacherNotes.privateNote") }}
                  </span>
                  <template v-else>
                    <span class="tnote__shares-label">
                      {{
                        $tc("teacherNotes.sharedWith", note.shares.length, {
                          count: note.shares.length,
                        })
                      }}
                    </span>
                    <span
                      v-for="share in note.shares"
                      :key="share.customerId"
                      class="chip"
                    >
                      {{ share.name || $t("teacherNotes.studentFallback") }}
                      <button
                        type="button"
                        :title="$t('teacherNotes.revokeTitle')"
                        :disabled="saving"
                        @click="revoke(note, share)"
                      >
                        <i class="mdi mdi-close"></i>
                      </button>
                    </span>
                  </template>
                </div>

                <div class="tnote__actions">
                  <button
                    v-if="!isTraining"
                    type="button"
                    @click="openShareForExisting(note)"
                  >
                    <i class="mdi mdi-account-multiple-plus-outline"></i>
                    {{ $t("teacherNotes.share") }}
                  </button>
                  <button
                    v-if="!isTraining"
                    type="button"
                    @click="openExtraPickerForExisting(note)"
                  >
                    <i class="mdi mdi-book-plus-outline"></i>
                    {{ $t("extraContent.add") }}
                  </button>
                  <button type="button" @click="startEdit(note)">
                    <i class="mdi mdi-pencil-outline"></i>
                    {{ $t("common.edit") }}
                  </button>
                  <button type="button" class="is-danger" @click="remove(note)">
                    <i class="mdi mdi-trash-can-outline"></i>
                    {{ $t("common.delete") }}
                  </button>
                </div>
              </template>
            </article>

            <p v-if="!isTraining" class="board__disclaimer">
              {{ $t("teacherNotes.disclaimer") }}
            </p>
          </div>

          <div v-else class="board board--empty">
            {{ $t("teacherNotes.empty") }}
          </div>
        </div>

        <!-- Retorno de 18/10 (card 17trt6jcyfb): exercícios da aula com o
             gabarito, só para consulta. Retorno de 22/10: no treinamento o
             professor faz os exercícios, sem ver o gabarito antes. -->
        <lesson-answer-key
          v-if="!isTraining && moduleId"
          :course-class-id="moduleId"
        />
        <lesson-exercises
          v-if="isTraining && moduleId"
          :course-class-id="moduleId"
          profile="partner"
        />
        <lesson-games
          v-if="!isTraining && moduleId"
          :course-class-id="moduleId"
          audience="partner"
        />
      </template>
    </LessonContent>

    <ExtraContentPicker
      v-model="extraPicker"
      :selected="extraPickerSelected"
      :busy="saving"
      @confirm="onExtrasPicked"
    />

    <ShareNoteDialog
      v-model="shareDialog"
      :groups="groups"
      :busy="saving"
      :title="shareDialogTitle"
      :confirm-label="shareDialogConfirmLabel"
      :already-shared-with="pendingShareTargets"
      @confirm="onShareConfirm"
    />
  </div>
</template>

<script>
import LessonContent from "@/components/connect/course/LessonContent.vue";
import ShareNoteDialog from "@/components/course/ShareNoteDialog.vue";
import ExtraContentPicker from "@/components/course/ExtraContentPicker.vue";
import LinkifiedText from "@/components/course/LinkifiedText.vue";
import LessonAnswerKey from "@/components/exercises/LessonAnswerKey.vue";
import LessonGames from "@/components/exercises/LessonGames.vue";
import LessonExercises from "@/components/exercises/LessonExercises.vue";
import pitchPartnerApi from "@/shared/pitchPartnerApi";

const MAX_LENGTH = 500;
// Espelha PARTNER_CLASS_NOTE_MAX_FILES e a lista de mimetypes da API: falhar
// aqui evita um round-trip que o backend recusaria de qualquer jeito.
const MAX_FILES = 5;
// Card 86ajhmfhb: "pdf, doc ou jpg", no máximo 10 MB.
const MAX_FILE_SIZE_MB = 10;
const ACCEPTED_TYPES = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".gif",
  ".doc",
  ".docx",
].join(",");

export default {
  name: "CourseContentPage",
  components: {
    LessonContent,
    ShareNoteDialog,
    ExtraContentPicker,
    LinkifiedText,
    LessonAnswerKey,
    LessonGames,
    LessonExercises,
  },
  async created() {
    await this.loadContent();
  },
  data() {
    return {
      module: {},
      moduleId: "",
      // Card 86akptec0: aula de treinamento do professor (conclusão própria,
      // anotação só privada).
      isTraining: false,
      completed: false,
      completing: false,
      loading: true,
      saving: false,
      maxLength: MAX_LENGTH,
      draft: "",
      notes: [],
      groups: [],
      shareDialog: false,
      // Anotação alvo do dialog: null quando o professor está criando uma nova
      shareTarget: null,
      // Card 86akj6ghg: tipo do rascunho no composer -- "note" ou "homework".
      draftKind: "note",
      editingId: "",
      editingText: "",
      // Card 17trt6jfxtn: aulas indicadas no rascunho e a anotação alvo do
      // seletor (null = o rascunho do composer).
      draftExtras: [],
      extraPicker: false,
      extraTarget: null,
      // Anexos já enviados ao bucket e ainda não vinculados a uma anotação.
      draftFiles: [],
      uploading: false,
      maxFiles: MAX_FILES,
      maxFileSizeMb: MAX_FILE_SIZE_MB,
      acceptedTypes: ACCEPTED_TYPES,
    };
  },
  computed: {
    requestedMaterialId() {
      return this.$route.query.material || "";
    },
    pendingShareTargets() {
      if (!this.shareTarget) return [];
      return this.shareTarget.shares.map((share) => share.customerId);
    },
    extraPickerSelected() {
      return this.extraTarget
        ? (this.extraTarget.extras || []).map((extra) => extra.contentId)
        : this.draftExtras.map((extra) => extra.id);
    },
    isNewHomework() {
      return !this.shareTarget && this.draftKind === "homework";
    },
    shareDialogTitle() {
      return this.isNewHomework
        ? this.$t("teacherNotes.homeworkTitle")
        : this.$t("shareNote.title");
    },
    shareDialogConfirmLabel() {
      return this.isNewHomework
        ? this.$t("teacherNotes.send")
        : this.$t("shareNote.confirm");
    },
  },
  methods: {
    async loadContent() {
      this.loading = true;

      const moduleId = this.$route.params.id;
      const parentModuleId = this.$route.params.module || moduleId;

      await pitchPartnerApi
        .getCourseContent(parentModuleId, moduleId)
        .then((res) => {
          this.moduleId = moduleId;
          this.module = res.data.content;
          this.isTraining = !!res.data.isTraining;
          this.completed = !!res.data.completed;
        })
        .catch(() => {
          this.alert(this.$t("teacherNotes.loadFailed"), 1);
        });

      await Promise.all([this.loadNotes(), this.loadGroups()]);
      this.loading = false;
    },
    async loadNotes() {
      if (!this.moduleId) return;

      await pitchPartnerApi
        .listCourseNotes(this.moduleId)
        .then((res) => {
          this.notes = res.data.notes || [];
        })
        .catch(() => {
          this.notes = [];
        });
    },
    async loadGroups() {
      await pitchPartnerApi
        .getStudyGroups()
        .then((res) => {
          this.groups = res.data || [];
        })
        .catch(() => {
          this.groups = [];
        });
    },
    async setCompletion(completed) {
      this.completing = true;

      await pitchPartnerApi
        .setTrainingCompletion(this.moduleId, completed)
        .then((res) => {
          this.completed = !!res.data.completed;
          this.alert(
            this.completed
              ? this.$t("lessonPage.lessonCompleted")
              : this.$t("lessonPage.lessonReopened")
          );
        })
        .catch((error) => {
          this.alert(
            this.apiMessage(error, this.$t("lessonPage.completionFailed")),
            1
          );
        });

      this.completing = false;
    },
    alert(text, type = 0) {
      this.$root.$emit("display-alert", { text, type, timeout: 2000 });
    },
    // A API devolve o motivo em `message` (ex.: aluno que saiu da turma). Erro
    // de validação vem como lista e cai no texto genérico.
    apiMessage(error, fallback) {
      const message = error?.response?.data?.message;
      return typeof message === "string" && message ? message : fallback;
    },
    draftFileIds() {
      return this.draftFiles.map((file) => file.id);
    },
    draftExtraIds() {
      return this.draftExtras.map((extra) => extra.id);
    },
    openExtraPickerForNew() {
      this.extraTarget = null;
      this.extraPicker = true;
    },
    openExtraPickerForExisting(note) {
      this.extraTarget = note;
      this.extraPicker = true;
    },
    removeDraftExtra(extra) {
      this.draftExtras = this.draftExtras.filter(
        (item) => item.id !== extra.id
      );
    },
    // Anotação já salva: reenvia o conjunto inteiro de extras. Quem já recebeu
    // a anotação é avisado só dos extras novos.
    async onExtrasPicked(picked) {
      if (!this.extraTarget) {
        this.draftExtras = picked;
        this.extraPicker = false;
        return;
      }

      this.saving = true;
      const note = this.extraTarget;

      await pitchPartnerApi
        .updateCourseNote(
          note.id,
          note.note,
          undefined,
          picked.map((extra) => extra.id)
        )
        .then(() => {
          this.extraPicker = false;
          this.alert(this.$t("extraContent.updated"));
          return this.loadNotes();
        })
        .catch((error) => {
          this.alert(
            this.apiMessage(error, this.$t("extraContent.updateFailed")),
            1
          );
        });

      this.saving = false;
    },
    async detachExtra(note, extra) {
      this.saving = true;

      const remaining = (note.extras || [])
        .filter((item) => item.id !== extra.id)
        .map((item) => item.contentId);

      await pitchPartnerApi
        .updateCourseNote(note.id, note.note, undefined, remaining)
        .then(() => {
          this.alert(this.$t("extraContent.removed"));
          return this.loadNotes();
        })
        .catch(() => {
          this.alert(this.$t("extraContent.updateFailed"), 1);
        });

      this.saving = false;
    },
    pickFiles() {
      this.$refs.fileInput.click();
    },
    // Upload em duas etapas: a API valida tipo/tamanho e devolve a URL assinada,
    // e o arquivo sobe direto no bucket. O id só vira anexo quando a anotação
    // for salva — arquivo enviado e anotação abandonada não polui a lista.
    async onFilesPicked(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";

      if (!file) return;

      if (this.draftFiles.length >= this.maxFiles) {
        this.alert(
          this.$t("teacherNotes.maxFiles", { count: this.maxFiles }),
          1
        );
        return;
      }

      if (file.size > this.maxFileSizeMb * 1024 * 1024) {
        this.alert(
          this.$t("teacherNotes.tooBig", { size: this.maxFileSizeMb }),
          1
        );
        return;
      }

      this.uploading = true;

      try {
        const res = await pitchPartnerApi.requestMaterialUploadUrl(file);
        await pitchPartnerApi.uploadMaterialToSignedUrl(res.data.url, file);
        this.draftFiles.push({ id: res.data.fileId, name: file.name });
      } catch (error) {
        this.alert(
          this.apiMessage(error, this.$t("teacherNotes.uploadFailed")),
          1
        );
      } finally {
        this.uploading = false;
      }
    },
    removeDraftFile(file) {
      this.draftFiles = this.draftFiles.filter((item) => item.id !== file.id);
    },
    // Desanexar de uma anotação já salva é reenviar o conjunto sem aquele id.
    async detachFile(note, file) {
      this.saving = true;

      const remaining = (note.files || [])
        .filter((item) => item.id !== file.id)
        .map((item) => item.id);

      await pitchPartnerApi
        .updateCourseNote(note.id, note.note, remaining)
        .then(() => {
          this.alert(this.$t("teacherNotes.detached"));
          return this.loadNotes();
        })
        .catch(() => {
          this.alert(this.$t("teacherNotes.detachFailed"), 1);
        });

      this.saving = false;
    },
    openShareForNew() {
      if (!this.draft.trim()) return;
      this.shareTarget = null;
      this.draftKind = "note";
      this.shareDialog = true;
    },
    // Card 86akj6ghg: reaproveita o mesmo diálogo de seleção; só o tipo muda.
    openHomeworkForNew() {
      if (!this.draft.trim()) return;
      this.shareTarget = null;
      this.draftKind = "homework";
      this.shareDialog = true;
    },
    openShareForExisting(note) {
      this.shareTarget = note;
      this.shareDialog = true;
    },
    async onShareConfirm(selection) {
      this.saving = true;

      const isHomework = this.isNewHomework;

      const request = this.shareTarget
        ? pitchPartnerApi.shareCourseNote(this.shareTarget.id, selection)
        : pitchPartnerApi.createCourseNote(this.moduleId, {
            note: this.draft.trim(),
            share: selection,
            fileIds: this.draftFileIds(),
            extraContentIds: this.draftExtraIds(),
            kind: this.draftKind,
          });

      await request
        .then(() => {
          if (!this.shareTarget) {
            this.draft = "";
            this.draftFiles = [];
            this.draftExtras = [];
            this.draftKind = "note";
          }
          this.shareDialog = false;
          this.alert(
            isHomework
              ? this.$t("teacherNotes.homeworkSent")
              : this.$t("teacherNotes.noteShared")
          );
          return this.loadNotes();
        })
        .catch((error) => {
          this.alert(
            this.apiMessage(
              error,
              isHomework
                ? this.$t("teacherNotes.homeworkFailed")
                : this.$t("teacherNotes.shareFailed")
            ),
            1
          );
        });

      this.saving = false;
    },
    async createPrivate() {
      if (!this.draft.trim()) return;
      this.saving = true;

      await pitchPartnerApi
        .createCourseNote(this.moduleId, {
          note: this.draft.trim(),
          fileIds: this.draftFileIds(),
          extraContentIds: this.draftExtraIds(),
        })
        .then(() => {
          this.draft = "";
          this.draftFiles = [];
          this.draftExtras = [];
          this.alert(this.$t("teacherNotes.savedPrivate"));
          return this.loadNotes();
        })
        .catch(() => {
          this.alert(this.$t("lessonPage.noteFailed"), 1);
        });

      this.saving = false;
    },
    startEdit(note) {
      this.editingId = note.id;
      this.editingText = note.note;
    },
    cancelEdit() {
      this.editingId = "";
      this.editingText = "";
    },
    async saveEdit(note) {
      this.saving = true;

      await pitchPartnerApi
        .updateCourseNote(note.id, this.editingText.trim())
        .then(() => {
          this.cancelEdit();
          this.alert(this.$t("teacherNotes.updatedAll"));
          return this.loadNotes();
        })
        .catch(() => {
          this.alert(this.$t("teacherNotes.updateFailed"), 1);
        });

      this.saving = false;
    },
    async remove(note) {
      this.saving = true;

      await pitchPartnerApi
        .deleteCourseNote(note.id)
        .then(() => {
          this.alert(this.$t("teacherNotes.deleted"));
          return this.loadNotes();
        })
        .catch(() => {
          this.alert(this.$t("teacherNotes.deleteFailed"), 1);
        });

      this.saving = false;
    },
    async revoke(note, share) {
      this.saving = true;

      await pitchPartnerApi
        .revokeCourseNoteShare(note.id, share.customerId)
        .then(() => {
          this.alert(
            this.$t("teacherNotes.revoked", {
              name: share.name || this.$t("teacherNotes.studentFallback"),
            })
          );
          return this.loadNotes();
        })
        .catch(() => {
          this.alert(this.$t("teacherNotes.revokeFailed"), 1);
        });

      this.saving = false;
    },
    goBack() {
      this.$router.back();
    },
  },
  watch: {
    "$route.fullPath"() {
      this.loadContent();
    },
  },
};
</script>

<style scoped>
.content-page {
  padding: clamp(16px, 3vw, 32px) clamp(16px, 4vw, 40px) 0;
}
.content-page__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  font-family: "Poppins", sans-serif;
  color: var(--dk-ink-soft, #6e6a79);
}

.composer {
  border-radius: 20px;
  border: 1px solid rgba(236, 72, 153, 0.18);
  background: var(--dk-surface, #fff);
  padding: 1.1rem 1.2rem;
  margin-bottom: 1.25rem;
}

.composer h4 {
  margin: 0;
  font-weight: 700;
  color: var(--dk-ink, #111827);
}

.composer p {
  margin: 0.2rem 0 0.75rem;
  color: rgba(31, 41, 55, 0.68);
}

.composer textarea {
  width: 100%;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  padding: 0.75rem 0.9rem;
  resize: vertical;
  outline: none;
  font: inherit;
}

.composer__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.6rem;
  flex-wrap: wrap;
}

.composer__count {
  color: rgba(31, 41, 55, 0.58);
  font-size: 0.85rem;
}

.composer__actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.composer__btn {
  border: none;
  border-radius: 999px;
  padding: 0.5rem 1.1rem;
  background: var(--dk-pink, #db2777);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
}

.composer__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.composer__btn--ghost {
  background: transparent;
  color: var(--dk-pink, #db2777);
  border: 1px solid rgba(219, 39, 119, 0.4);
}

/* Card 86akj6ghg: cor própria porque é outro destino na tela do aluno, não
   uma variação de ênfase do mesmo botão. */
.extras {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin: 10px 0 4px;
}
.extras__add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px dashed var(--dk-line, #cfc9d8);
  border-radius: 999px;
  background: transparent;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--dk-ink, #231a2e);
  cursor: pointer;
}
.tnote__extras {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
}
.composer__btn--homework {
  background: #4f46e5;
}

.tnote__kind {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 0.5rem;
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(79, 70, 229, 0.12);
  color: #4338ca;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.board {
  display: grid;
  gap: 0.9rem;
}

.board--empty {
  border: 1px dashed rgba(236, 72, 153, 0.24);
  border-radius: 20px;
  padding: 2.5rem 1rem;
  text-align: center;
  color: rgba(31, 41, 55, 0.58);
}

.board__disclaimer {
  margin: 0.25rem 0 0;
  font-size: 0.82rem;
  color: rgba(31, 41, 55, 0.55);
}

.tnote {
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: var(--dk-paper, #f8fafc);
  padding: 1rem 1.1rem;
}

.tnote__body {
  white-space: pre-wrap;
  margin: 0 0 0.7rem;
  color: var(--dk-ink, #1f2937);
  font-weight: 600;
  line-height: 1.45;
}

.tnote__shares {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.7rem;
}

.tnote__shares-label,
.tnote__private {
  color: rgba(31, 41, 55, 0.62);
  font-size: 0.85rem;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: var(--dk-surface, #fff);
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 999px;
  padding: 0.15rem 0.5rem 0.15rem 0.7rem;
  font-size: 0.85rem;
}

.chip small {
  color: rgba(31, 41, 55, 0.5);
}

.chip button {
  border: none;
  background: none;
  cursor: pointer;
  color: rgba(31, 41, 55, 0.5);
  line-height: 1;
}

.tnote__actions {
  display: flex;
  gap: 0.9rem;
  flex-wrap: wrap;
}

.tnote__actions button,
.tnote__edit-actions button {
  border: none;
  background: none;
  cursor: pointer;
  font-weight: 600;
  color: var(--dk-pink, #db2777);
  padding: 0;
}

.tnote__actions .is-danger {
  color: #b91c1c;
}

.tnote__edit textarea {
  width: 100%;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  padding: 0.75rem 0.9rem;
  font: inherit;
  resize: vertical;
}

.tnote__edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 0.5rem;
}

.tnote__edit-actions .is-primary {
  color: var(--dk-pink, #db2777);
}
.attachments {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.attachments__input {
  display: none;
}
.attachments__add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px dashed rgba(var(--dk-ink-rgb, 35, 26, 46), 0.28);
  border-radius: 999px;
  background: transparent;
  font-size: 0.8rem;
  cursor: pointer;
}
.attachments__add:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.attachments__hint {
  font-size: 0.72rem;
  color: var(--dk-muted, #6b6472);
}
.attachments__list {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.attachments__list li {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(168, 38, 225, 0.08);
  font-size: 0.78rem;
}
.attachments__name {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.attachments__list button {
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 1;
}
.tnote__files {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.tnote__files li {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
}
.tnote__files a {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--dk-grape, #a826e1);
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tnote__files a:hover {
  text-decoration: underline;
}
.tnote__files button {
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 1;
  opacity: 0.6;
}
.tnote__files button:hover {
  opacity: 1;
}
</style>
